import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { changeLocalPassword } from '@/contexts/auth/localAuth';

const SENHA_MIN = 8;

interface AlterarSenhaDialogProps {
  email: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Troca de senha do próprio usuário local (Supabase Auth). */
const AlterarSenhaDialog: React.FC<AlterarSenhaDialogProps> = ({ email, open, onOpenChange }) => {
  const { toast } = useToast();
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const fechar = () => {
    setAtual('');
    setNova('');
    setConfirmacao('');
    setErro(null);
    onOpenChange(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (nova.length < SENHA_MIN) return setErro(`A nova senha deve ter ao menos ${SENHA_MIN} caracteres.`);
    if (nova !== confirmacao) return setErro('As senhas não conferem.');

    setSalvando(true);
    const result = await changeLocalPassword(email, atual, nova);
    setSalvando(false);
    if (!result.success) return setErro(result.message ?? 'Não foi possível alterar a senha.');

    toast({ title: 'Senha alterada', description: 'Use a nova senha no próximo acesso.' });
    fechar();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && fechar()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Alterar senha</DialogTitle>
          <DialogDescription>Informe a senha atual e a nova senha.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="senha-atual">Senha atual</Label>
            <Input id="senha-atual" type="password" autoComplete="current-password" value={atual} onChange={(e) => setAtual(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="senha-nova">Nova senha</Label>
            <Input id="senha-nova" type="password" autoComplete="new-password" value={nova} onChange={(e) => setNova(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="senha-confirmacao">Confirmar nova senha</Label>
            <Input id="senha-confirmacao" type="password" autoComplete="new-password" value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} />
          </div>
          {erro && <p className="text-sm text-red-600">{erro}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={fechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Alterar senha'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AlterarSenhaDialog;
